import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE, page, cardScroll, inp, btn } from './AdminUsers';
import TopBanner from '../components/TopBanner';
import AdminSidebar from '../components/AdminSidebar';

// User-requested companion to AdminActiveSessions.jsx — "which app was
// accessed, by how many people, how often, over a week/month/custom
// range" (historical), as opposed to that page's live-right-now view.
// Sourced from Authentik's own already-logged authorize_application
// events via backend/src/routes/adminAppUsage.js — see that file's own
// header comment for why (reuses existing data, no new app-side
// instrumentation needed, unlike the live per-app breakdown which needed
// every connected app's own heartbeat call updated).

// FIXED (2026-10-17): used to be `d.toISOString().slice(0, 10)`, which
// converts to UTC first — for an IST (UTC+5:30) admin, clicking "Today"
// any time before 5:30am sent YESTERDAY's date, and the backend's own
// UTC-midnight date parsing compounded it further (see adminAppUsage.js's
// own fix). Local Y/M/D components instead — this assumes the admin's
// own browser clock is sensibly set to IST, which the backend no longer
// relies on (it anchors from/to explicitly to IST regardless of what
// string arrives), but this still needs to send the date the admin
// ACTUALLY means by "today" in their own local calendar.
function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function startOfWeek(d) {
  const x = new Date(d);
  const day = x.getDay(); // 0=Sun
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
}
function startOfMonth(d) {
  const x = new Date(d.getFullYear(), d.getMonth(), 1);
  return x;
}

const PRESETS = [
  { key: 'today', label: 'Today', from: () => startOfDay(new Date()) },
  { key: 'week', label: 'This week', from: () => startOfWeek(new Date()) },
  { key: 'month', label: 'This month', from: () => startOfMonth(new Date()) },
  { key: 'custom', label: 'Custom' },
];

const thStyle = {
  textAlign: 'left', padding: '10px 12px', fontSize: '12px', fontWeight: '700',
  color: '#666', textTransform: 'uppercase', letterSpacing: '0.04em',
  borderBottom: '2px solid #eee', background: '#fafafa',
};
const tdStyle = { padding: '10px 12px', fontSize: '13px', color: '#2c2c2a', borderBottom: '1px solid #f0f0f0', verticalAlign: 'middle' };

// Pure-CSS bar chart — no charting library in this codebase, and this is
// simple enough (one bar per day, height proportional to this APP's own
// max day) not to need one. Scrolls horizontally rather than squeezing
// bars illegibly thin for a month+ range.
function Sparkline({ daily }) {
  if (!daily.length) return <span style={{ color: '#aaa', fontSize: '12px' }}>No activity</span>;
  const max = Math.max(...daily.map((d) => d.count), 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: '36px', overflowX: 'auto', maxWidth: '280px' }}>
      {daily.map((d) => (
        <div
          key={d.date}
          title={`${d.date}: ${d.count}`}
          style={{
            width: '6px', flexShrink: 0, borderRadius: '2px 2px 0 0',
            background: '#009FE3',
            height: `${Math.max(3, Math.round((d.count / max) * 32))}px`,
          }}
        />
      ))}
    </div>
  );
}

// Simple overlay modal listing who accessed an app in the selected
// period, sorted by their own access count (already sorted server-side —
// see adminAppUsage.js). No library — this codebase doesn't use one for
// modals elsewhere either (AdminUserDetail.jsx etc. all hand-roll them
// the same way).
function UserListModal({ app, onClose }) {
  if (!app) return null;
  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: '12px', padding: '20px 24px', width: '360px', maxHeight: '70vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
          <h2 style={{ fontSize: '16px', fontWeight: '700', margin: 0, color: '#1a1a1a' }}>{app.name}</h2>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#888', padding: '0 4px' }}>×</button>
        </div>
        <p style={{ fontSize: '12px', color: '#888', margin: '0 0 14px' }}>
          {app.users.length} distinct user{app.users.length === 1 ? '' : 's'}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {app.users.map((u) => (
            <div key={u.username} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 4px', fontSize: '13px', borderBottom: '1px solid #f4f4f4' }}>
              <span style={{ color: '#2c2c2a' }}>{u.username}</span>
              <span style={{ color: '#888' }}>{u.count}×</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function AdminAppUsage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [preset, setPreset] = useState('today');
  const [customFrom, setCustomFrom] = useState(isoDate(startOfWeek(new Date())));
  const [customTo, setCustomTo] = useState(isoDate(new Date()));

  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [range, setRange] = useState(null); // { from, to } the server actually used, for the "Showing X–Y" line
  const [userListFor, setUserListFor] = useState(null); // the app row whose Distinct Users list is open, or null

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/');
      return;
    }
    const groups = user.profile?.groups || [];
    if (!groups.includes('bescom-super-admins')) {
      navigate('/dashboard');
    }
  }, [user, authLoading, navigate]);

  const effectiveFrom = preset === 'custom' ? customFrom : isoDate(PRESETS.find((p) => p.key === preset).from());
  const effectiveTo = preset === 'custom' ? customTo : isoDate(new Date());

  const fetchUsage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ from: effectiveFrom, to: effectiveTo });
      const res = await fetch(`${API_BASE}/admin/app-usage?${params}`, { credentials: 'include' });
      if (res.status === 401) {
        navigate('/', { state: { sessionExpired: true } });
        return;
      }
      if (res.status === 403) {
        setError('You are not authorized to view app usage.');
        return;
      }
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Could not load app usage.');
        return;
      }
      setApps(data.apps || []);
      setRange({ from: data.from, to: data.to });
    } catch (e) {
      setError('Could not reach the identity service.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, effectiveFrom, effectiveTo]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  const totalAccessAll = apps.reduce((sum, a) => sum + a.totalAccess, 0);

  return (
    <>
      <TopBanner />
      <div className="admin-page-wrap" style={page}>
        <div className="admin-row" style={{ display: 'flex', gap: '24px', width: '100%', height: '100%' }}>
          <AdminSidebar active="app-usage" />
          <div style={cardScroll}>
            <h1 style={{ fontSize: '20px', fontWeight: '700', color: '#1a1a1a', margin: '0 0 4px' }}>
              App Usage
            </h1>
            <p style={{ fontSize: '13px', color: '#666', marginBottom: '20px' }}>
              Which connected apps were accessed, by how many people, and how often — over a selected period.
              Sourced from Authentik's own sign-in events, so it covers every connected app automatically.
            </p>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
              {PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setPreset(p.key)}
                  style={{
                    ...btn, width: 'auto', padding: '7px 16px', fontSize: '13px',
                    background: preset === p.key ? undefined : '#fff',
                    color: preset === p.key ? undefined : '#333',
                    border: preset === p.key ? 'none' : '1px solid #ddd',
                  }}
                >
                  {p.label}
                </button>
              ))}
            </div>

            {preset === 'custom' && (
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' }}>
                <label style={{ fontSize: '13px', color: '#666' }}>
                  From{' '}
                  <input type="date" value={customFrom} max={customTo} onChange={(e) => setCustomFrom(e.target.value)} style={{ ...inp, width: 'auto', padding: '6px 10px' }} />
                </label>
                <label style={{ fontSize: '13px', color: '#666' }}>
                  To{' '}
                  <input type="date" value={customTo} min={customFrom} max={isoDate(new Date())} onChange={(e) => setCustomTo(e.target.value)} style={{ ...inp, width: 'auto', padding: '6px 10px' }} />
                </label>
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', alignItems: 'center' }}>
              <button type="button" onClick={fetchUsage} style={{ ...btn, width: 'auto', padding: '8px 16px', fontSize: '13px' }}>
                ↻ Refresh
              </button>
              {range && (
                <span style={{ fontSize: '12px', color: '#888' }}>
                  Showing {new Date(range.from).toLocaleDateString()} – {new Date(range.to).toLocaleDateString()}
                  {!loading && ` · ${totalAccessAll} total access${totalAccessAll === 1 ? '' : 'es'} across ${apps.length} app${apps.length === 1 ? '' : 's'}`}
                </span>
              )}
            </div>

            {error && (
              <div style={{ background: '#FDECEC', color: '#B3261E', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px' }}>
                {error}
              </div>
            )}

            {loading ? (
              <p style={{ textAlign: 'center', color: '#666', fontSize: '14px' }}>Loading…</p>
            ) : apps.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#666', padding: '40px' }}>
                <p style={{ fontSize: '32px', margin: '0 0 16px' }}>📊</p>
                <p>No app activity in this period.</p>
              </div>
            ) : (
              <div style={{ border: '1px solid #eee', borderRadius: '10px', overflow: 'hidden', overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>App</th>
                      <th style={thStyle}>Total Access</th>
                      <th style={thStyle}>Distinct Users</th>
                      <th style={thStyle}>Daily trend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {apps.map((a) => (
                      <tr key={a.name}>
                        <td style={{ ...tdStyle, fontWeight: '600' }}>{a.name}</td>
                        <td style={tdStyle}>{a.totalAccess}</td>
                        <td style={tdStyle}>
                          <span
                            onClick={() => setUserListFor(a)}
                            style={{ color: '#0072B1', cursor: 'pointer', textDecoration: 'underline' }}
                          >
                            {a.distinctUsers}
                          </span>
                        </td>
                        <td style={tdStyle}><Sparkline daily={a.daily} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
      <UserListModal app={userListFor} onClose={() => setUserListFor(null)} />
    </>
  );
}
